import { useEffect, useRef, useState } from "react";
import { Phone, Flame, Leaf, ShieldCheck, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RESTAURANT, TAG_LEGEND } from "../data/restaurant";
import { useCart } from "../context/CartContext";
import { useMenu } from "../context/MenuContext";
import VariantPicker, { itemTakesSpice, spiceLabel, DEFAULT_SPICE } from "../components/VariantPicker";

const tagStyles = {
  veg: "bg-[#374F3B]/10 text-[#374F3B]",
  gf: "bg-[#0E0E1A]/10 text-[#0E0E1A]",
  nuts: "bg-[#A16207]/10 text-[#A16207]",
  spicy: "bg-[#C9853E]/10 text-[#C9853E]",
  signature: "bg-[#E5B07D]/15 text-[#E5B07D]",
};
const tagLabels = { veg: "Veg", gf: "GF", nuts: "Nuts", spicy: "Spicy", signature: "Signature" };

const TagPill = ({ tag }) => {
  const Icon = tag === "spicy" ? Flame : tag === "veg" ? Leaf : tag === "gf" ? ShieldCheck : null;
  return (
    <span
      data-testid={`tag-${tag}`}
      className={`inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded-full ${tagStyles[tag]}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {tagLabels[tag]}
    </span>
  );
};

export default function MenuPage() {
  const { categories, loading, error } = useMenu();
  const [active, setActive] = useState(null);
  const [pickerItem, setPickerItem] = useState(null);
  const { add } = useCart();
  const sectionRefs = useRef({});
  const navRef = useRef(null);

  const activeSlug = active || categories[0]?.slug;

  // Scroll-spy: pick the section whose top is closest to (but at/above)
  // the area just below the sticky nav. Uses rAF for smooth tracking.
  useEffect(() => {
    if (!categories.length) return;
    let raf = 0;
    let userClicked = false;
    let clickTimeout = 0;

    // Trigger line just below the sticky nav. As soon as a section's top
    // crosses above this line, it becomes active.
    const triggerY = 160; // 80 header + 56 nav + ~24 buffer

    const update = () => {
      raf = 0;
      if (userClicked) return;
      const els = categories
        .map((c) => sectionRefs.current[c.slug])
        .filter(Boolean);
      if (!els.length) return;

      let currentSlug = els[0].dataset.slug;
      for (const el of els) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerY) {
          currentSlug = el.dataset.slug;
        } else {
          break;
        }
      }
      // Edge case: bottom of page — pick the last section if scrolled to bottom
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 80
      ) {
        currentSlug = els[els.length - 1].dataset.slug;
      }
      setActive((prev) => (prev === currentSlug ? prev : currentSlug));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    // Expose a method to suppress scroll-spy briefly during programmatic scroll.
    sectionRefs.current.__suppress = (ms = 700) => {
      userClicked = true;
      clearTimeout(clickTimeout);
      clickTimeout = setTimeout(() => {
        userClicked = false;
        update();
      }, ms);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(clickTimeout);
    };
  }, [categories]);

  // Sliding indicator position behind the active pill.
  const [indicator, setIndicator] = useState({ left: 0, top: 0, width: 0, height: 0, ready: false });

  // Keep the active button visible in the horizontal scroll bar — manual,
  // smooth scrollLeft animation (mobile Safari is unreliable with scrollIntoView).
  useEffect(() => {
    if (!activeSlug || !navRef.current) return;
    const nav = navRef.current;
    const btn = nav.querySelector(`[data-slug="${activeSlug}"]`);
    if (!btn) return;

    // Match the indicator exactly to the active pill's box.
    setIndicator({
      left: btn.offsetLeft,
      top: btn.offsetTop,
      width: btn.offsetWidth,
      height: btn.offsetHeight,
      ready: true,
    });

    // Smoothly scroll the pill into the centre of the nav
    const target = btn.offsetLeft - nav.clientWidth / 2 + btn.offsetWidth / 2;
    const start = nav.scrollLeft;
    const delta = target - start;
    if (Math.abs(delta) < 8) return;
    const duration = 280;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      nav.scrollLeft = start + delta * ease(t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [activeSlug, categories]);

  // Recompute indicator on resize
  useEffect(() => {
    const onResize = () => {
      const nav = navRef.current;
      if (!nav || !activeSlug) return;
      const btn = nav.querySelector(`[data-slug="${activeSlug}"]`);
      if (btn) {
        setIndicator({
          left: btn.offsetLeft,
          top: btn.offsetTop,
          width: btn.offsetWidth,
          height: btn.offsetHeight,
          ready: true,
        });
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeSlug]);

  const handleAdd = (item) => {
    if (item.price == null && !item.variants?.length) return;
    // Open picker if the item has variants, a spice level, OR a free-dip
    // choice (tandoori grills fall into this last bucket — no variants and
    // spice disabled, but the customer must still pick a dip).
    if (item.variants?.length || itemTakesSpice(item) || item.dip_choice) {
      setPickerItem(item);
      return;
    }
    add({
      item_id: item.id,
      name: item.name,
      variant_name: null,
      variant_label: null,
      price: item.price,
    });
    toast.success(`Added ${item.name} to cart`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="menu-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9853E] mr-2" />
        <span className="text-[#57534E]">Loading menu…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-center px-6" data-testid="menu-error">
        <div>
          <div className="font-display text-2xl font-bold text-[#0E0E1A]">
            We couldn't load the menu
          </div>
          <p className="text-[#57534E] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="menu-page" className="bg-[#FAF8F5]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10">
        <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-4">
          The Menu
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <h1 className="md:col-span-7 font-display text-5xl md:text-7xl font-extrabold leading-[1.02] text-[#0E0E1A]">
            Built for<br /> bold appetites.
          </h1>
          <p className="md:col-span-5 text-[#57534E] text-lg leading-relaxed">
            {categories.length} categories, {categories.reduce((s, c) => s + c.items.length, 0)}+ dishes.
            Add anything to your cart and check out — pay online or at pickup.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="tag-legend">
          <span className="text-xs text-[#57534E] font-semibold mr-1">Legend:</span>
          {TAG_LEGEND.map((t) => (
            <TagPill key={t.key} tag={t.key} />
          ))}
        </div>
      </section>

      <div
        data-testid="menu-category-nav"
        className="sticky top-20 z-30 bg-[#FAF8F5]/95 backdrop-blur-xl border-y border-[#E7E5E4]"
      >
        <div
          ref={navRef}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-3 flex gap-2 overflow-x-auto scrollbar-hide"
        >
          {/* Sliding indicator — animates between active pills (rendered behind text) */}
          <div
            data-testid="nav-indicator"
            aria-hidden
            className={`absolute rounded-full bg-[#0E0E1A] transition-[left,top,width,height] duration-[320ms] ease-[cubic-bezier(0.4,0.0,0.2,1)] pointer-events-none ${
              indicator.ready ? "opacity-100" : "opacity-0"
            }`}
            style={{
              left: `${indicator.left}px`,
              top: `${indicator.top}px`,
              width: `${indicator.width}px`,
              height: `${indicator.height}px`,
              zIndex: 0,
            }}
          />
          {categories.map((c) => {
            const isActive = activeSlug === c.slug;
            return (
              <button
                key={c.slug}
                data-testid={`menu-cat-${c.slug}`}
                data-slug={c.slug}
                onClick={() => {
                  setActive(c.slug);
                  sectionRefs.current.__suppress?.(900);
                  document.getElementById(`cat-${c.slug}`)?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }}
                className={`relative whitespace-nowrap px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-bold transition-colors duration-200 ${
                  isActive
                    ? "text-white bg-transparent border border-transparent"
                    : "bg-white border border-[#E7E5E4] text-[#0E0E1A] hover:border-[#0E0E1A]"
                }`}
                style={{ zIndex: 1 }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {categories.map((cat) => (
        <section
          key={cat.slug}
          id={`cat-${cat.slug}`}
          ref={(el) => (sectionRefs.current[cat.slug] = el)}
          data-slug={cat.slug}
          data-testid={`menu-section-${cat.slug}`}
          className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 scroll-mt-32"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5 relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[280px] bg-[#0E0E1A]">
              {cat.image_url && (
                <img src={cat.image_url} alt={cat.name} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E1A]/70 via-[#0E0E1A]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <div className="text-xs tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-1">
                  Category
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white">
                  {cat.name}
                </h2>
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-6 flex flex-col justify-center">
              <p className="font-accent italic text-2xl md:text-3xl text-[#0E0E1A] leading-snug">
                {cat.intro}
              </p>
              {cat.slug === "lunch-special" && (
                <p className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-extrabold text-white bg-[#0E0E1A] rounded-full px-4 py-2 self-start">
                  Available for Dine-In Only
                </p>
              )}
              {cat.note && (
                <p className="mt-4 text-sm text-[#57534E] bg-white border border-[#E7E5E4] rounded-2xl px-4 py-3 inline-block">
                  {cat.note}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
            {cat.items.map((item, i) => {
              const dineInOnly = cat.slug === "lunch-special";
              const canOrder = !dineInOnly && (item.price != null || item.variants?.length);
              return (
                <article
                  key={item.id}
                  data-testid={`menu-item-${cat.slug}-${i}`}
                  className="group flex gap-4 pb-6 border-b border-dashed border-[#E7E5E4]"
                >
                  {item.image_data && (
                    <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 bg-[#E7E5E4]">
                      <img src={item.image_data} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1.5 mb-1.5">
                      <h3 className="font-display text-xl md:text-2xl font-bold text-[#0E0E1A]">
                        {item.name}
                      </h3>
                      {item.tags?.map((t) => (
                        <TagPill key={t} tag={t} />
                      ))}
                    </div>
                    {item.desc && (
                      <p className="text-[#57534E] leading-relaxed text-[15px]">{item.desc}</p>
                    )}
                    {canOrder && (
                      <button
                        data-testid={`menu-add-${cat.slug}-${i}`}
                        onClick={() => handleAdd(item)}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#C9853E] hover:text-white hover:bg-[#C9853E] border border-[#C9853E] rounded-full px-3.5 py-1.5 transition-all"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add to cart
                      </button>
                    )}
                    {dineInOnly && (
                      <span
                        data-testid={`menu-dinein-${cat.slug}-${i}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase font-bold text-[#0E0E1A] bg-[#FAF8F5] border border-[#E7E5E4] rounded-full px-3.5 py-1.5"
                      >
                        For Dine-In Only
                      </span>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {item.price != null ? (
                      <div className="font-display text-xl md:text-2xl font-bold text-[#C9853E]">
                        ${item.price.toFixed(2)}
                      </div>
                    ) : (
                      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#57534E] bg-[#FAF8F5] border border-[#E7E5E4] rounded-full px-2.5 py-1">
                        {item.note || "See online"}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <section className="bg-[#0E0E1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold">Have questions?</h2>
            <p className="text-white/70 mt-2">
              Give us a call — we're happy to help with allergies, custom orders, or large parties.
            </p>
          </div>
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            data-testid="menu-order-cta"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white rounded-full px-7 py-4 font-bold transition-all"
          >
            <Phone className="w-4 h-4" /> Call {RESTAURANT.phone}
          </a>
        </div>
      </section>

      <VariantPicker
        item={pickerItem}
        open={!!pickerItem}
        onClose={() => setPickerItem(null)}
        onAdd={(v) => {
          add(v);
          const parts = [v.name];
          if (v.variant_label) parts.push(`(${v.variant_label})`);
          if (v.spice_level && v.spice_level !== DEFAULT_SPICE) parts.push(`· ${spiceLabel(v.spice_level)}`);
          toast.success(`Added ${parts.join(" ")} to cart`);
        }}
      />
    </div>
  );
}
